import React, { Suspense, lazy, useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'

// Initialise Sentry only when DSN is provided — zero cost when not configured
if (import.meta.env.VITE_SENTRY_DSN) {
  import('@sentry/react').then(Sentry => {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.2,
    })
  }).catch(() => {})
}
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import '@fontsource-variable/plus-jakarta-sans/wght.css'
import '@fontsource/dm-mono/400.css'
import '@fontsource/dm-mono/500.css'
import { useAuthStore } from './stores/auth'
import { ToastProvider } from './components/Toast'
import Tour from './components/Tour'
import Layout from './components/Layout'
import BrandLogo from './components/BrandLogo'
import NetworkStatus from './components/NetworkStatus'

const NotFound = lazy(() => import('./pages/NotFound'))
const Login = lazy(() => import('./pages/Login'))
const MicrosoftCallback = lazy(() => import('./pages/MicrosoftCallback'))
const PendingAccess = lazy(() => import('./pages/PendingAccess'))
const IncomingAssignments = lazy(() => import('./pages/reviewer/IncomingAssignments'))
const ReviewerOverview    = lazy(() => import('./pages/reviewer/ReviewerOverview'))
const VVOverview          = lazy(() => import('./pages/VVOverview'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Datasets = lazy(() => import('./pages/Datasets'))
const RuleManager = lazy(() => import('./pages/RuleManager'))
const RunDQA = lazy(() => import('./pages/RunDQA'))
const Violations = lazy(() => import('./pages/Violations'))
const CorrectionWorkbench = lazy(() => import('./pages/CorrectionWorkbench'))
const CorrectionRules = lazy(() => import('./pages/CorrectionRules'))
const AIHub = lazy(() => import('./pages/AIHub'))
const AuditTrail = lazy(() => import('./pages/AuditTrail'))
const VVDashboard = lazy(() => import('./pages/VVDashboard'))
const AnomalyDetection = lazy(() => import('./pages/AnomalyDetection'))
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'))
const ModelHub = lazy(() => import('./pages/ModelHub'))
const VVProject    = lazy(() => import('./pages/VVProject'))
const VVPortal     = lazy(() => import('./pages/VVPortal'))
const VVBenchmarks = lazy(() => import('./pages/VVBenchmarks'))
const Reports = lazy(() => import('./pages/Reports'))
const Trends = lazy(() => import('./pages/Trends'))
const Schedules = lazy(() => import('./pages/Schedules'))
const Settings = lazy(() => import('./pages/Settings'))
const UserManual = lazy(() => import('./pages/UserManual'))
const UserManagement = lazy(() => import('./pages/UserManagement'))
const ProjectsPage = lazy(() => import('./pages/Projects'))
const AIChat = lazy(() => import('./pages/AIChat'))
const StatusPage    = lazy(() => import('./pages/StatusPage'))
const Onboarding    = lazy(() => import('./pages/Onboarding'))
const ResetPassword    = lazy(() => import('./pages/ResetPassword'))
const ProtocolManager  = lazy(() => import('./pages/ProtocolManager'))
const RuleStudio       = lazy(() => import('./pages/RuleStudio'))
const ReviewerDashboard      = lazy(() => import('./pages/reviewer/ReviewerDashboard'))
const ActiveReview           = lazy(() => import('./pages/reviewer/ActiveReview'))
const CARManagement          = lazy(() => import('./pages/reviewer/CARManagement'))
const VerificationStatements = lazy(() => import('./pages/reviewer/VerificationStatements'))
const IntegrationHub         = lazy(() => import('./pages/reviewer/IntegrationHub'))
const RegistryConnectors     = lazy(() => import('./pages/reviewer/RegistryConnectors'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 12_000),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      networkMode: 'online',
    },
    mutations: { retry: 0, networkMode: 'online' },
  },
})

function RouteFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading page"
      className="fade-in"
      style={{
        padding: 36,
        maxWidth: 520,
        margin: '0 auto',
        fontFamily: 'var(--font-body, system-ui, sans-serif)',
        color: 'var(--text-muted)',
        fontSize: 13,
      }}
    >
      <div className="ds-skel" style={{ height: 10, width: 140, borderRadius: 6, marginBottom: 16 }} />
      <div className="ds-skel" style={{ height: 8, width: '100%', borderRadius: 6, marginBottom: 10 }} />
      <div className="ds-skel" style={{ height: 8, width: '92%', borderRadius: 6, marginBottom: 10 }} />
      <div className="ds-skel" style={{ height: 8, width: '70%', borderRadius: 6 }} />
    </div>
  )
}

// ── Error Boundary ────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: any) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // F034: log full technical details for developers / Sentry
    console.error('[ErrorBoundary]', error, info.componentStack)
    // Report to Sentry when DSN is configured
    if (import.meta.env.VITE_SENTRY_DSN) {
      import('@sentry/react').then(Sentry => Sentry.captureException(error)).catch(() => {})
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', background: 'var(--surface-0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-body, system-ui, sans-serif)', padding: 40 }}>
          <div style={{ maxWidth: 600, textAlign: 'center' }}>
            <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
              <BrandLogo size={48} />
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--canyon)', marginBottom: 8, fontFamily: 'var(--font-display, system-ui, sans-serif)', letterSpacing: -0.5 }}>DataSentinel</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, fontFamily: 'var(--font-display, system-ui, sans-serif)' }}>Something went wrong</div>
            {/* F034: show generic message to user; log full error to console for developers */}
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
              An unexpected error occurred. Please reload the page. If the problem persists, contact support.
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{ padding: '10px 28px', background: 'var(--canyon)', color: 'var(--off-white)', border: 'none', borderRadius: 'var(--r-sm)', cursor: 'pointer', fontSize: 14, fontWeight: 700, boxShadow: 'var(--shadow-canyon)' }}
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// ── Auth guard ────────────────────────────────────────────────────────────────
function Guard({ children }: { children: React.ReactNode }) {
  const user = useAuthStore(s => s.user)
  const initialized = useAuthStore(s => s.initialized)
  // Wait for init() to finish before deciding — avoids flash-redirect on page load
  if (!initialized) return null
  if (!user) return <Navigate to="/login" replace />

  // Platform access check — if user has explicit platform_access set and ALL are false,
  // redirect to the pending-access holding page.
  // Admin and super_admin always bypass this check.
  const isAdmin = user.role === 'admin' || user.role === 'super_admin'
  if (!isAdmin && user.platform_access) {
    const values = Object.values(user.platform_access)
    const hasAnyAccess = values.some(v => v === true)
    if (!hasAnyAccess && window.location.pathname !== '/pending') {
      return <Navigate to="/pending" replace />
    }
  }

  return <>{children}</>
}

// ── App shell ─────────────────────────────────────────────────────────────────
function Inner() {
  // F024: use `user` (populated from cookie) not `token` (always null by design)
  const user = useAuthStore(s => s.user)
  const [showTour, setShowTour] = useState(false)

  useEffect(() => {
    if (!user) return
    if (!localStorage.getItem('ds_tour_done')) {
      const t = setTimeout(() => setShowTour(true), 800)
      return () => clearTimeout(t)
    }
  }, [user])

  // Redirect new users to onboarding on first ever login
  useEffect(() => {
    if (!user) return
    const key = 'ds_onboarding_seen'
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, '1')
      // Give the app a moment to mount, then redirect
      const t = setTimeout(() => {
        if (window.location.pathname === '/dashboard' || window.location.pathname === '/') {
          window.history.pushState({}, '', '/onboarding')
          window.dispatchEvent(new PopStateEvent('popstate'))
        }
      }, 1500)
      return () => clearTimeout(t)
    }
  }, [user])

  useEffect(() => {
    const h = () => setShowTour(true)
    window.addEventListener('startTour', h)
    return () => window.removeEventListener('startTour', h)
  }, [])

  return (
    <>
      <NetworkStatus />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login"          element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback"  element={<MicrosoftCallback />} />
          <Route path="/pending"        element={<PendingAccess />} />
          <Route path="/" element={<Guard><Layout /></Guard>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard"       element={<Dashboard />} />
            <Route path="projects"        element={<ProjectsPage />} />
            <Route path="datasets"        element={<Datasets />} />
            <Route path="rule-studio"     element={<RuleStudio />} />
            <Route path="rules"           element={<RuleManager />} />
            <Route path="run"             element={<RunDQA />} />
            <Route path="violations"      element={<Violations />} />
            <Route path="corrections"     element={<CorrectionWorkbench />} />
            <Route path="correctionrules" element={<CorrectionRules />} />
            <Route path="ai"              element={<AIHub />} />
            <Route path="trends"          element={<Trends />} />
            <Route path="schedules"       element={<Schedules />} />
            <Route path="settings"        element={<Settings />} />
            <Route path="users"           element={<UserManagement />} />
            <Route path="audit"           element={<AuditTrail />} />
            <Route path="anomaly"         element={<AnomalyDetection />} />
            <Route path="knowledge-base" element={<KnowledgeBase />} />
            <Route path="vv"              element={<VVOverview />} />
            <Route path="vv/projects"     element={<VVDashboard />} />
            <Route path="vv/project/:id"  element={<VVProject />} />
            <Route path="vv/portal/:id"   element={<VVPortal />} />
            <Route path="vv/benchmarks"   element={<VVBenchmarks />} />
            <Route path="reports"         element={<Reports />} />
            <Route path="manual"          element={<UserManual />} />
            <Route path="ai-chat"         element={<AIChat />} />
            <Route path="status"          element={<StatusPage />} />
            <Route path="onboarding"      element={<Onboarding />} />
            <Route path="protocols"       element={<ProtocolManager />} />
            <Route path="model-hub"       element={<ModelHub />} />
            <Route path="reviewer"             element={<ReviewerOverview />} />
            <Route path="reviewer/incoming"    element={<IncomingAssignments />} />
            <Route path="reviewer/assignments" element={<ReviewerDashboard />} />
            <Route path="reviewer/review/:id"  element={<ActiveReview />} />
            <Route path="reviewer/cars"        element={<CARManagement />} />
            <Route path="reviewer/statements"  element={<VerificationStatements />} />
            <Route path="reviewer/integration" element={<IntegrationHub />} />
            <Route path="reviewer/connectors"  element={<RegistryConnectors />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {showTour && <Tour onComplete={() => setShowTour(false)} />}
    </>
  )
}

function App() {
  const init = useAuthStore(s => s.init)
  useEffect(() => { init() }, [init])
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <Inner />
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <App />
)

function mountBackgroundParticles() {
  if (typeof document === 'undefined') return
  if (document.querySelector('.bg-particles')) return
  const wrap = document.createElement('div')
  wrap.className = 'bg-particles'
  const positions = [
    { x: 8, y: 15, s: 6, a: 'float1 8s ease-in-out infinite' },
    { x: 92, y: 8, s: 5, a: 'float2 10s ease-in-out infinite 1s' },
    { x: 15, y: 85, s: 8, a: 'float3 12s ease-in-out infinite 2s' },
    { x: 88, y: 78, s: 5, a: 'float1 9s ease-in-out infinite 3s' },
    { x: 50, y: 5, s: 4, a: 'float2 11s ease-in-out infinite 0.5s' },
    { x: 5, y: 50, s: 6, a: 'float3 13s ease-in-out infinite 4s' },
    { x: 95, y: 45, s: 5, a: 'float1 10s ease-in-out infinite 2.5s' },
    { x: 35, y: 92, s: 4, a: 'float2 8s ease-in-out infinite 1.5s' },
    { x: 72, y: 18, s: 7, a: 'float3 14s ease-in-out infinite 0.8s' },
    { x: 25, y: 35, s: 3, a: 'float1 9s ease-in-out infinite 3.5s' },
    { x: 78, y: 62, s: 4, a: 'float2 11s ease-in-out infinite 2s' },
    { x: 45, y: 70, s: 5, a: 'float3 10s ease-in-out infinite 1.2s' },
  ]
  positions.forEach(p => {
    const el = document.createElement('span')
    el.style.cssText = `left:${p.x}%;top:${p.y}%;width:${p.s}px;height:${p.s}px;animation:${p.a};`
    wrap.appendChild(el)
  })
  document.body.insertBefore(wrap, document.body.firstChild)
}

mountBackgroundParticles()
